"use client";

import { useEffect, useRef, useState } from "react";
import AppImage from "@/components/ui/AppImage";
import { homepageService, HeroStat } from "@/lib/services/homepageService";
import Link from "next/link";
import React from "react";


export default function HeroSection() {
  const heroRef = useRef<HTMLDivElement>(null);
  const [heroImageUrl, setHeroImageUrl] = useState<string>(
    "https://gogedit.ca/www_images/2026/03/gogedit-homepage-hero-1024-1024.png"
  );
  const [stats, setStats] = useState<HeroStat[]>([
    { icon: "A→B", label: "Any Item", value: "A→B" },
    { icon: "Real-Time", label: "Tracking", value: "Real-Time" },
    { icon: "On-Demand", label: "Right Now", value: "On-Demand" },
  ]);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [statsError, setStatsError] = useState<string | null>(null);

  useEffect(() => {
    const el = heroRef?.current;
    if (!el) return;
    el?.classList?.add("animate-slide-left");

    // Fetch hero image from database
    const loadHeroImage = async () => {
      try {
        const heroContent = await homepageService?.getByKey('hero');
        if (heroContent?.content?.heroImageUrl) {
          setHeroImageUrl(heroContent?.content?.heroImageUrl);
        }
      } catch (error) {
        console.error('Failed to load hero image:', error);
      }
    };

    // Fetch hero stats from database
    const loadHeroStats = async () => {
      try {
        setIsLoadingStats(true);
        setStatsError(null);
        const fetchedStats = await homepageService?.getHeroStats();
        if (fetchedStats && fetchedStats.length > 0) {
          setStats(fetchedStats);
        }
      } catch (error) {
        console.error('Failed to load hero stats:', error);
        setStatsError('Failed to load stats');
      } finally {
        setIsLoadingStats(false);
      }
    };

    loadHeroImage();
    loadHeroStats();
  }, []);

  return (
    <section className="hero-gradient relative overflow-hidden min-h-[calc(100vh-80px)] flex items-center">
      {/* Background decorative circles */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-brand-purple/5 -translate-y-1/4 translate-x-1/4 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-brand-green/5 translate-y-1/4 -translate-x-1/4 pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 w-[800px] h-[800px] rounded-full bg-accent-purple/3 -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 w-full">
        <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
          {/* Left content */}
          <div ref={heroRef} className="space-y-6 sm:space-y-8 opacity-0 min-w-0">
            <div className="section-label">
              <span className="w-2 h-2 rounded-full bg-brand-green inline-block" />
              Redefining Urban Logistics
            </div>

            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-[64px] font-bold leading-[1.1] tracking-tight text-deep-navy">
              Who&apos;s Ready to{" "}
              <span className="text-brand-green">GoGedit?</span>
            </h1>

            <p className="text-lg sm:text-xl md:text-2xl font-semibold text-dark-navy leading-snug max-w-lg">
              We won&apos;t move you or your house...{" "}
              <span className="text-body-text font-normal">Just everything else.</span>
            </p>

            <p className="text-body-text text-base sm:text-lg leading-relaxed max-w-md">
              The on demand, right when you need it service that fills the gap where all
              those other services fail on size, quality and attention to detail.
            </p>

            {/* Stats row */}
            <div className="flex flex-wrap items-center gap-4 sm:gap-8 py-4">
              {isLoadingStats ? (
                <div className="flex items-center gap-2 text-muted-text">
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="text-sm">Loading stats...</span>
                </div>
              ) : statsError ? (
                <div className="text-sm text-red-600">{statsError}</div>
              ) : (
                stats.map((stat, index) => (
                  <React.Fragment key={`stat-${index}`}>
                    {index > 0 && <div className="w-px h-10 bg-border-gray hidden sm:block" />}
                    <div className="text-center">
                      <p className={`font-heading font-800 text-2xl ${index % 2 === 0 ? 'text-brand-purple' : 'text-brand-green'}`}>
                        {stat.value}
                      </p>
                      <p className="text-xs text-muted-text font-medium uppercase tracking-wider mt-1">
                        {stat.label}
                      </p>
                    </div>
                  </React.Fragment>
                ))
              )}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4 pt-2" id="signup-user">
              <Link href="/be-a-user" className="btn-primary">
                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                Sign-Up — Be a User
              </Link>
              <Link href="/be-a-driver" className="btn-green" id="signup-driver">
                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <rect x="1" y="3" width="15" height="13" rx="2" />
                  <path d="M16 8h4l3 5v3h-7V8z" />
                  <circle cx="5.5" cy="18.5" r="2.5" />
                  <circle cx="18.5" cy="18.5" r="2.5" />
                </svg>
                Sign-Up — Be a Driver
              </Link>
            </div>

            {/* Trust badges */}
            <div className="flex items-center gap-4 pt-2">
              <div className="flex -space-x-2">
                {[
                  { src: "/assets/images/trust-badge-profile-1.png", alt: "Smiling young woman with brown hair — GoGedit driver" },
                  { src: "/assets/images/trust-badge-profile-2.png", alt: "Smiling man with short dark hair and beard — GoGedit driver" },
                  { src: "/assets/images/trust-badge-profile-3.png", alt: "Smiling young Black woman with natural hair — GoGedit driver" },
                  { src: "/assets/images/trust-badge-profile-4.png", alt: "Smiling Asian man in his 30s — GoGedit driver" },
                ].map((profile, i) => (
                  <div
                    key={i}
                    className="w-9 h-9 rounded-full border-2 border-white overflow-hidden">
                    <AppImage
                      src={profile.src}
                      alt={profile.alt}
                      width={36}
                      height={36}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
              <p className="text-sm text-body-text">
                <span className="font-bold text-dark-navy">100+</span> drivers across Ontario
              </p>
            </div>
          </div>

          {/* Right content — phone mockup + floating cards */}
          <div className="relative flex justify-center lg:justify-end overflow-hidden lg:overflow-visible">
            <div className="relative w-full max-w-[420px]">
              {/* Glow behind phone */}
              <div className="absolute inset-0 bg-brand-purple/10 rounded-full blur-3xl scale-75" />

              {/* Phone image */}
              <div className="relative z-10 animate-float">
                <AppImage
                  src={heroImageUrl}
                  alt="GoGedit app on mobile phone — hand holding phone with company logo"
                  width={782}
                  height={825}
                  className="w-full h-auto max-w-[380px] mx-auto drop-shadow-2xl"
                  priority />
                
              </div>

              {/* Floating card — Live tracking */}
              <div
                className="card-glass absolute top-12 -left-4 lg:-left-8 rounded-2xl px-4 py-3 shadow-purple-md z-20 animate-float-slow"
                style={{ animationDelay: "1s" }}>
                
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-brand-green/15 flex items-center justify-center">
                    <svg width="20" height="20" fill="none" stroke="#48794D" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-deep-navy">Live Tracking</p>
                    <p className="text-[11px] text-body-text">Driver 2 min away</p>
                  </div>
                  <div className="w-2 h-2 rounded-full bg-brand-green animate-pulse ml-1" />
                </div>
              </div>

              {/* Floating card — Deal secured */}
              <div
                className="card-glass absolute top-1/3 -right-4 lg:-right-10 rounded-2xl px-4 py-3 shadow-purple-md z-20 animate-float"
                style={{ animationDelay: "2s" }}>
                
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-brand-purple/15 flex items-center justify-center">
                    <svg width="20" height="20" fill="none" stroke="#4732D7" strokeWidth="2" viewBox="0 0 24 24">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-deep-navy">Pickup Confirmed</p>
                    <p className="text-[11px] text-body-text">Large sofa — Brampton</p>
                  </div>
                </div>
              </div>

              {/* Floating card — Rating */}
              <div
                className="card-glass absolute bottom-24 -left-2 lg:-left-6 rounded-2xl px-4 py-3 shadow-purple-md z-20 animate-float-slow"
                style={{ animationDelay: "0.5s" }}>
                
                <div className="flex items-center gap-2">
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5]?.map((s) =>
                    <svg key={s} width="12" height="12" fill="#F59E0B" viewBox="0 0 24 24">
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                      </svg>
                    )}
                  </div>
                  <p className="text-xs font-bold text-deep-navy">5.0 Rated</p>
                </div>
                <p className="text-[11px] text-body-text mt-0.5">by 200+ customers</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Bottom wave */}
      <div className="wavy-divider absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
          <path d="M0 60L60 50C120 40 240 20 360 15C480 10 600 20 720 25C840 30 960 30 1080 25C1200 20 1320 10 1380 5L1440 0V60H0Z" fill="white" />
        </svg>
      </div>
    </section>);

}